import React, { createContext, CSSProperties, useCallback, useContext, useEffect, useState } from 'react'
import { UniqueIdentifier } from '@dnd-kit/core'
import { Candidate } from '../candidate/candidate'
import { Column } from '../campaign/column'

export type ColumnData = {
    [key: string]: {
        column: Column
    }
}

interface DragData {
    item: Candidate
    fromColumn: string
    index: number
}

interface ColumnDragData {
    type: 'column'
    columnId: string
}

interface DropLinePosition {
    columnId: string
    index: number
}

interface BoardContextType {
    columns: ColumnData
    setColumns: React.Dispatch<React.SetStateAction<ColumnData>>
    columnOrder: string[]
    setColumnOrder: React.Dispatch<React.SetStateAction<string[]>>
    activeItem: DragData | null
    setActiveItem: React.Dispatch<React.SetStateAction<DragData | null>>
    activeColumn: ColumnDragData | null
    setActiveColumn: React.Dispatch<React.SetStateAction<ColumnDragData | null>>
    overId: UniqueIdentifier | null
    setOverId: React.Dispatch<React.SetStateAction<UniqueIdentifier | null>>
    dropLinePosition: DropLinePosition | null
    setDropLinePosition: React.Dispatch<React.SetStateAction<DropLinePosition | null>>
    isColumnOver: (columnId: string) => boolean
    showAttributes?: boolean
    showPhone?: boolean
    campaignId?: string
    dragTransform?: CSSProperties['transform']
    selected: Record<string, boolean>
    setSelected: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
    columnCandidates: Record<string, Candidate[]>
    setColumnCandidates: React.Dispatch<React.SetStateAction<Record<string, Candidate[]>>>
    moveCandidateOptimistically: (candidateId: string, fromColumn: string, toColumn: string, newOrder: string) => void
    hasPendingOptimisticUpdate: boolean
    setHasPendingOptimisticUpdate: React.Dispatch<React.SetStateAction<boolean>>
}

const BoardContext = createContext<BoardContextType | undefined>(undefined)

export const useBoardContext = () => {
    const context = useContext(BoardContext)
    if (!context) {
        throw new Error('useBoardContext must be used within a BoardProvider')
    }
    return context
}

export const BoardProvider: React.FC<{
    initialColumns: ColumnData
    children: React.ReactNode
    showAttributes?: boolean
    showPhone?: boolean
    campaignId?: string
    selected: Record<string, boolean>
    setSelected: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
}> = ({ initialColumns, showAttributes, showPhone, campaignId, children, selected, setSelected }) => {
    const [columns, setColumns] = useState<ColumnData>(initialColumns)
    const [columnOrder, setColumnOrder] = useState<string[]>(Object.keys(initialColumns))
    const [activeItem, setActiveItem] = useState<DragData | null>(null)
    const [activeColumn, setActiveColumn] = useState<ColumnDragData | null>(null)
    const [overId, setOverId] = useState<UniqueIdentifier | null>(null)
    const [dropLinePosition, setDropLinePosition] = useState<DropLinePosition | null>(null)
    const [columnCandidates, setColumnCandidates] = useState<Record<string, Candidate[]>>({})
    const [hasPendingOptimisticUpdate, setHasPendingOptimisticUpdate] = useState(false)

    useEffect(() => {
        setColumns(initialColumns)
        setColumnOrder(Object.keys(initialColumns))
    }, [initialColumns])

    const isColumnOver = useCallback(
        (columnId: string) => {
            return overId === `column-${columnId}`
        },
        [overId]
    )

    const moveCandidateOptimistically = useCallback(
        (candidateId: string, fromColumn: string, toColumn: string, newOrder: string) => {
            setColumnCandidates((prev) => {
                // Find the candidate in the source column
                const sourceColumn = prev[fromColumn] || []
                const candidate = sourceColumn.find((c) => c.id === candidateId)

                if (!candidate) return prev

                // Remove from source column
                const newSourceColumn = sourceColumn.filter((c) => c.id !== candidateId)

                const orderColumn = campaignId ? 'columnOrder' : 'companyColumnOrder'
                const idColumn = campaignId ? 'columnId' : 'companyColumnId'

                // Add to target column with updated order
                // For same-column moves, use the filtered array to avoid duplicates
                const targetColumn = fromColumn === toColumn ? newSourceColumn : prev[toColumn] || []
                const updatedCandidate = {
                    ...candidate,
                    [idColumn]: toColumn,
                    [orderColumn]: newOrder,
                }

                const newTargetColumn = [...targetColumn, updatedCandidate].sort((a, b) => {
                    const orderA = a[orderColumn] || ''
                    const orderB = b[orderColumn] || ''
                    return orderA < orderB ? -1 : orderA > orderB ? 1 : 0
                })

                // For same-column moves, only update one key
                if (fromColumn === toColumn) {
                    return {
                        ...prev,
                        [toColumn]: newTargetColumn,
                    }
                }

                return {
                    ...prev,
                    [fromColumn]: newSourceColumn,
                    [toColumn]: newTargetColumn,
                }
            })
        },
        [campaignId]
    )

    return (
        <BoardContext.Provider
            value={{
                columns,
                setColumns,
                columnOrder,
                setColumnOrder,
                activeItem,
                setActiveItem,
                activeColumn,
                setActiveColumn,
                overId,
                setOverId,
                dropLinePosition,
                setDropLinePosition,
                isColumnOver,
                showAttributes,
                showPhone,
                campaignId,
                dragTransform: `rotate(4deg)`,
                selected,
                setSelected,
                columnCandidates,
                setColumnCandidates,
                moveCandidateOptimistically,
                hasPendingOptimisticUpdate,
                setHasPendingOptimisticUpdate,
            }}
        >
            {children}
        </BoardContext.Provider>
    )
}
